import { slicedToArray as _slicedToArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useRef, useState, useEffect } from 'react';
import { Box, Heading, Image } from '@zillow/constellation';
import { AnimatePresence, motion } from 'framer-motion';
import PropTypes from 'prop-types';

var animationsDefaultTimingDuration = '0.2s';
var ProgressBar = function ProgressBar(_ref) {
  var active = _ref.active,
    animationTime = _ref.animationTime,
    skipAnimation = _ref.skipAnimation;
  var BAR_WIDTH = '6px';
  var BORDER_RADIUS = 'calc($radii.sm / 2)';
  var INACTIVE_COLOR = '$colors.gray200';
  var ACTIVE_COLOR = '$colors.brand';

  // Use `hasBeenActive` to avoid triggering the up slide animation on bars that have
  // never been active on the first place
  var _useState = useState(active),
    _useState2 = _slicedToArray(_useState, 2),
    hasBeenActive = _useState2[0],
    setHasBeenActive = _useState2[1];
  useEffect(function () {
    if (active && !hasBeenActive) {
      setHasBeenActive(true);
    }
  }, [active, hasBeenActive]);
  var css = {
    width: BAR_WIDTH,
    height: '100%',
    'background-color': !active && skipAnimation || !hasBeenActive ? INACTIVE_COLOR : ACTIVE_COLOR,
    'border-radius': BORDER_RADIUS
  };
  if (active && !skipAnimation) {
    css = Object.assign({}, css, {
      animation: "progressAnimationStrike " + animationTime + "s",
      'animation-timing-function': '$animations.defaultTimingFunction',
      '@keyframes progressAnimationStrike': {
        from: {
          height: '0%'
        },
        to: {
          height: '100%'
        }
      }
    });
  } else if (!active && !skipAnimation && hasBeenActive) {
    css = {
      width: BAR_WIDTH,
      'border-radius': BORDER_RADIUS,
      'background-color': '$colors.brand',
      animation: "inActiveProgressAnimationStrike " + animationsDefaultTimingDuration,
      'animation-timing-function': '$animations.defaultTimingFunction',
      '@keyframes inActiveProgressAnimationStrike': {
        from: {
          height: '100%'
        },
        to: {
          height: '0%'
        }
      }
    };
  }
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      'background-color': INACTIVE_COLOR,
      'border-radius': BORDER_RADIUS
    }
  }, /*#__PURE__*/React.createElement(Box, {
    sx: css
  }));
};
ProgressBar.propTypes = process.env.NODE_ENV !== "production" ? {
  active: PropTypes.bool,
  animationTime: PropTypes.number,
  skipAnimation: PropTypes.bool
} : {};
var ListItemWithProgress = function ListItemWithProgress(_ref2) {
  var active = _ref2.active,
    collapseOthers = _ref2.collapseOthers,
    animationTime = _ref2.animationTime,
    children = _ref2.children,
    heading = _ref2.heading,
    onClick = _ref2.onClick,
    skipAnimation = _ref2.skipAnimation;
  var divRef = useRef(null);
  return /*#__PURE__*/React.createElement(Box, {
    onClick: onClick,
    sx: {
      cursor: 'pointer',
      display: 'flex',
      gap: '$spacing.md',
      outline: 'none',
      '&:focus-visible': {
        boxShadow: '$shadows.focus'
      }
    },
    "aria-expanded": !collapseOthers || active,
    "aria-label": heading,
    "aria-selected": active
    // Make this div focusable and accessible
    ,
    role: "button",
    tabIndex: 0,
    ref: divRef,
    onKeyDown: function onKeyDown(e) {
      if (['Enter', 'Space'].includes(e.key)) {
        e.preventDefault();
        onClick(divRef.current);
      }
    }
  }, /*#__PURE__*/React.createElement(ProgressBar, {
    animationTime: animationTime,
    active: active,
    skipAnimation: skipAnimation
  }), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Heading, {
    as: "span",
    fontColor: active ? 'brand' : 'textDark',
    level: "5"
  }, heading), /*#__PURE__*/React.createElement(AnimatePresence, {
    exitBeforeEnter: true,
    initial: false
  }, (!collapseOthers || active) && /*#__PURE__*/React.createElement(motion.div, {
    key: "content"
    // Expand the height in and out
    ,
    initial: {
      height: 0,
      opacity: 0
    },
    animate: {
      height: 'auto',
      opacity: 1
    },
    exit: {
      height: 0,
      opacity: 0
    },
    style: {
      overflow: 'hidden'
    }
    // Value of animation tokens - https://constellation.zillowgroup.com/platforms/web/tokens/animation
    ,
    transition: {
      duration: 0.2,
      easing: 'easeOut'
    }
  }, children))));
};
ListItemWithProgress.propTypes = process.env.NODE_ENV !== "production" ? {
  active: PropTypes.bool,
  heading: PropTypes.string,
  paragraph: PropTypes.string,
  animationTime: PropTypes.number,
  onClick: PropTypes.func,
  skipAnimation: PropTypes.bool,
  collapseOthers: PropTypes.bool
} : {};
var imageCss = {
  objectFit: 'contain',
  'animation-timing-function': 'cubic - bezier(0.65, 0, 0.35, 1)',
  transition: animationsDefaultTimingDuration + " $animations.defaultTimingFunction",
  maxWidth: '100%',
  maxHeight: '100%'
};
var fadeInAnimation = {
  '@keyframes fadein': {
    from: {
      opacity: 0
    },
    to: {
      opacity: 1
    }
  },
  animation: "fadein " + animationsDefaultTimingDuration
};
var fadeoutAnimation = {
  opacity: 0
};
var ImageWithProgress = function ImageWithProgress(_ref3) {
  var active = _ref3.active,
    media = _ref3.media;
  var styles = Object.assign({}, imageCss, active ? fadeInAnimation : fadeoutAnimation);
  return /*#__PURE__*/React.createElement(Box, {
    as: Image,
    alt: imageAlt(media),
    src: imageSrc(media),
    width: media.width,
    height: media.height,
    sx: styles,
    "aria-hidden": !active
  });
};
ImageWithProgress.propTypes = process.env.NODE_ENV !== "production" ? {
  active: PropTypes.bool,
  media: PropTypes.object
} : {};
process.env.NODE_ENV !== "production" ? {
  active: PropTypes.bool,
  media: PropTypes.object
} : {};
function imageSrc(media, src) {
  return media ? media.src : src;
}
function imageAlt(media, alt) {
  return media && media.alt ? media.alt : alt;
}

export { ImageWithProgress, ListItemWithProgress, imageAlt, imageSrc };
